import { useMutation, useQuery } from '@tanstack/react-query';

import { getDiscountApprovers, getSystemDiscounts, postCustomerDiscounts } from 'queries';
import { parseErrors, Toast } from 'helpers';
import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { JsonData, JsonDataItem } from 'types';

const filterCustomerDiscounts = (data: JsonData) => {
  return data
    ?.filter((discount: JsonDataItem) => discount.type === 'Customer')
    .map((discount: JsonDataItem) => {
      return {
        discountId: discount.customer_discount_id,
        label: discount.discount_desc,
        amount: discount.amount,
        type: discount.discount_type,
        approvedBy: discount.approved_by_info?.name,
        details: discount,
      };
    });
};

export const useDiscountForm = (customerId: number | undefined) => {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [appliedDiscounts, setAppliedDiscounts] = useState<JsonData>();

  const { data: discountApproverList } = useQuery(['discount-approvers'], getDiscountApprovers, {
    onError: (error) => Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error }),
  });

  const { data: systemDiscounts, isInitialLoading: isDiscountsLoading } = useQuery(
    ['service-discounts', customerId],
    getSystemDiscounts,
    {
      onSuccess: (data) => setAppliedDiscounts(filterCustomerDiscounts(data)),
      onError: (error) => Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error }),
    },
  );

  const {
    mutate: addDiscountsMutation,
    isLoading: isDiscountsAdding,
    isSuccess: isSuccessfullyAdded,
  } = useMutation(postCustomerDiscounts, {
    onSuccess: (data) => setAppliedDiscounts(filterCustomerDiscounts(data)),
    onError: (data) => Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error }),
  });

  const addDiscounts = (formData: JsonDataItem | null) => {
    const discounts = formData?.discounts.map((discount: JsonDataItem) => ({
      system_discount_id: discount.discountId[1], // Cascader returns an array
      approved_by: discount.approvedBy,
      reason_id: discount.extraReason || null,
    }));

    addDiscountsMutation({ customerId, discounts });
  };

  const formatSavedDiscounts = (formData: JsonDataItem | null | undefined) =>
    formData?.discounts?.map((discount: JsonDataItem) => {
      const discountId = discount.discountId[1];
      const systemDiscount = systemDiscounts?.find(
        (systemDiscount: JsonDataItem) => systemDiscount.customer_discount_id === discount.discountId[1],
      );
      const approver = discountApproverList?.find(
        (approver: JsonDataItem) => approver.ID === parseInt(discount.approvedBy),
      );

      return {
        discountId: discountId,
        amount: systemDiscount?.amount,
        label: systemDiscount?.discount_desc,
        approvedBy: approver?.name,
      };
    });

  const values = Form.useWatch([], form);
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsFormValid(true))
      .catch(() => setIsFormValid(false));
  }, [form, values]);

  return {
    form,
    isFormValid,
    discountApproverList,
    systemDiscounts,
    isDiscountsLoading,
    isDiscountsAdding,
    addDiscounts,
    formatSavedDiscounts,
    appliedDiscounts,
    isSuccessfullyAdded,
  };
};
